import { useState, useEffect, useCallback, useRef } from 'react';
import { tickersApi } from '../api/client';

/**
 * Autocomplete ticker input with Yahoo/Finnhub-backed suggestions.
 *
 * Behaviour factored out of TradeModal and DividendModal:
 *  - Debounced search at >=1 char.
 *  - Upper-cases the value as the user types.
 *  - Dropdown with symbol, exchange/currency badges, company name, type/source row.
 *  - Closes the dropdown on outside click.
 *  - Forwards the full suggestion object to `onSelectSuggestion` so the parent
 *    can hydrate related fields (display_name, currency, ...).
 *
 * Props:
 *   value              - controlled input value (string)
 *   onChange(upper)    - called with the uppercased value on every keystroke
 *   onSelectSuggestion - called with the full suggestion object on pick
 *   placeholder        - optional placeholder text
 *   required           - optional, marks input as required
 *   rich               - when true, renders the Type/Source bottom row (trade modal style)
 */
function TickerSearchInput({
  value,
  onChange,
  onSelectSuggestion,
  placeholder = 'e.g. AAPL',
  required = false,
  rich = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  const searchTickers = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await tickersApi.search(query, 10);
      setSuggestions(response.results || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Ticker search error:', error);
      setSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const upper = e.target.value.toUpperCase();
    onChange(upper);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (upper.length >= 1) {
      searchTimeoutRef.current = setTimeout(() => searchTickers(upper), 150);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const pick = (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    onSelectSuggestion?.(suggestion);
  };

  useEffect(() => {
    const handleClickOutside = () => setShowSuggestions(false);
    if (showSuggestions) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSuggestions]);

  // Cleanup pending timeout on unmount.
  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        name="ticker"
        value={value}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      {searchLoading && (
        <div style={{ position: 'absolute', right: '10px', top: '35px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Searching...
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--bg-light)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            marginTop: '4px',
            maxHeight: '280px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {suggestions.map((s, idx) => (
            <div
              key={`${s.symbol || s.ticker}-${idx}`}
              onClick={() => pick(s)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: idx < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Main row: symbol + badges */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                  {s.symbol || s.ticker}
                  {s.is_confirmed && <span style={{ color: 'var(--primary)', marginLeft: '4px' }}>✓</span>}
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {s.exchange && (
                    <span style={{
                      fontSize: '0.65rem',
                      color: 'var(--text-muted)',
                      background: 'rgba(255,255,255,0.05)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                    }}>
                      {s.exchange}
                    </span>
                  )}
                  <span style={{
                    fontSize: '0.65rem',
                    color: 'var(--primary)',
                    background: 'rgba(0, 212, 255, 0.1)',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    fontWeight: 500,
                  }}>
                    {s.currency}
                  </span>
                </div>
              </div>

              {/* Company name */}
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                {s.long_name || s.short_name || s.name || s.display_name || ''}
              </div>

              {/* Bottom row: type + source */}
              {rich && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    {s.type || 'EQUITY'}
                  </span>
                  <span style={{
                    fontSize: '0.65rem',
                    color: s.source === 'CONFIRMED' ? 'var(--primary)' : 'var(--text-muted)',
                  }}>
                    {s.source}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TickerSearchInput;
